import React, { useState } from "react";
import { AlertCircle, Boxes, Rocket, Truck } from "lucide-react";
import useShippingPartners from "../hooks/useShippingPartners";
import { ParcelHubWarehouse, ShippingService } from "../services/shippingService";
import { ShippingPartner, ShippingPartnerType } from "../types/shippingPartner";
import useToastStore from "../store/useToastStore";

interface ModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

// Components
const Modal: React.FC<ModalProps> = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-semibold mb-4">{title}</h2>
                <p className="mb-6 text-gray-600">{message}</p>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

// Shipping partner UI configuration interface
interface ShippingPartnerUIConfig {
    type: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    iconBgColor: string;
    iconColor: string;
    requiresConfig: boolean;
    // field.type === "list" is rendered/stored as a string[] (a chip-list editor) instead of a plain string.
    // field.type === "warehouse" is stored as a number and rendered as a dropdown of the
    // partner's Parcel Hub warehouses, falling back to a plain number input.
    configFields?: {
        key: string;
        label: string;
        type: string;
        placeholder: string;
        example?: string;
    }[];
    infoMessage?: {
        title: string;
        message: string;
        bgColor: string;
        borderColor: string;
        textColor: string;
        iconColor: string;
    };
}

// Shipping partner configurations
const SHIPPING_PARTNER_CONFIGS: Record<string, ShippingPartnerUIConfig> = {
    DELHIVERY: {
        type: ShippingPartnerType.DELHIVERY,
        name: "Delhivery",
        description: "Integrate with Delhivery shipping service for automated delivery tracking and management",
        icon: <Truck className="h-5 w-5" />,
        iconBgColor: "bg-blue-100",
        iconColor: "text-blue-600",
        requiresConfig: true,
        configFields: [
            {
                key: "apiToken",
                label: "API Token",
                type: "password",
                placeholder: "Enter your Delhivery API Token"
            },
            {
                key: "pickupLocations",
                label: "Pickup Locations",
                type: "list",
                placeholder: "Enter a pickup location name",
                example: "Must exactly match a pickup location name registered in your Delhivery dashboard. Add one or more - you'll choose between them when shipping an order."
            }
        ],
        infoMessage: {
            title: "Integration Setup",
            message: "To integrate with Delhivery, you'll need to obtain an API token from your Delhivery dashboard. Once configured, you'll be able to create shipping labels, track packages, and manage deliveries directly from your admin panel.",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            textColor: "text-blue-700",
            iconColor: "text-blue-600"
        }
    },
    SHIPROCKET: {
        type: ShippingPartnerType.SHIPROCKET,
        name: "Shiprocket",
        description: "Integrate with Shiprocket shipping service for automated delivery tracking and management",
        icon: <Rocket className="h-5 w-5" />,
        iconBgColor: "bg-purple-100",
        iconColor: "text-purple-600",
        requiresConfig: true,
        configFields: [
            {
                key: "email",
                label: "Email",
                type: "text",
                placeholder: "Enter your Shiprocket account email"
            },
            {
                key: "password",
                label: "Password",
                type: "password",
                placeholder: "Enter your Shiprocket account password"
            },
            {
                key: "pickupLocations",
                label: "Pickup Locations",
                type: "list",
                placeholder: "Enter a pickup location nickname",
                example: "Must exactly match a pickup location nickname registered in your Shiprocket dashboard (Settings > Pickup Addresses). Add one or more - you'll choose between them when shipping an order."
            }
        ],
        infoMessage: {
            title: "Integration Setup",
            message: "To integrate with Shiprocket, enter the email and password for your Shiprocket account, and add the pickup location nickname(s) you've already created under Settings > Pickup Addresses in your Shiprocket dashboard. Once configured, you'll be able to create shipments and get tracking details directly from your admin panel.",
            bgColor: "bg-purple-50",
            borderColor: "border-purple-200",
            textColor: "text-purple-700",
            iconColor: "text-purple-600"
        }
    },
    PARCELHUB: {
        type: ShippingPartnerType.PARCELHUB,
        name: "Parcel Hub",
        description: "Integrate with Parcel Hub shipping service for automated delivery tracking and management",
        icon: <Boxes className="h-5 w-5" />,
        iconBgColor: "bg-amber-100",
        iconColor: "text-amber-600",
        requiresConfig: true,
        configFields: [
            {
                key: "apiKey",
                label: "API Key",
                type: "password",
                placeholder: "Enter your Parcel Hub API key"
            },
            {
                key: "accountEmail",
                label: "Account Email",
                type: "text",
                placeholder: "Enter your Parcel Hub account email",
                example: "The email your Parcel Hub account is registered under — not your customer's email."
            },
            {
                key: "sellerName",
                label: "Seller Name",
                type: "text",
                placeholder: "Enter the seller name to print on shipments"
            },
            {
                key: "sellerGstTin",
                label: "Seller GST / TIN",
                type: "text",
                placeholder: "Enter your GST or TIN number"
            },
            {
                key: "warehouseId",
                label: "Pickup Warehouse",
                type: "warehouse",
                placeholder: "Enter your Parcel Hub warehouse ID",
                example: "Save your API key and account email first, then reopen Edit to pick a warehouse from your Parcel Hub account."
            }
        ],
        infoMessage: {
            title: "Integration Setup",
            message: "To integrate with Parcel Hub, enter the API key and account email from your Parcel Hub dashboard, along with the seller name and GST/TIN to print on shipments. Save once, then reopen Edit to choose your pickup warehouse. Returns are sent to the same warehouse.",
            bgColor: "bg-amber-50",
            borderColor: "border-amber-200",
            textColor: "text-amber-700",
            iconColor: "text-amber-600"
        }
    }
    // Add more shipping partners here as needed
};

type ConfigValue = string | string[] | number;
type ConfigValues = Record<string, ConfigValue>;

const isBlank = (value: ConfigValue | Date | undefined): boolean => {
    if (typeof value === "number") return Number.isNaN(value);
    if (typeof value === "string") return value.trim() === "";
    return value == null;
};

// Warehouse fields are typed as numbers on the backend, so coerce before saving —
// the inputs all hand back strings.
const toConfigPayload = (
    values: ConfigValues,
    fields: ShippingPartnerUIConfig["configFields"]
): ShippingPartner["config"] => {
    const payload: Record<string, unknown> = { ...values };
    fields?.forEach(field => {
        if (field.type !== "warehouse") return;
        const value = values[field.key];
        payload[field.key] = isBlank(value) ? undefined : Number(value);
    });
    return payload as ShippingPartner["config"];
};

const warehouseLabel = (warehouse: ParcelHubWarehouse): string => {
    const name = warehouse.warehouse_name || warehouse.name || `Warehouse ${warehouse.warehouse_id}`;
    const place = [warehouse.city, warehouse.pincode].filter(Boolean).join(" ");
    return place ? `${name} — ${place}` : name;
};

// Unified Shipping Partner Card Component
const ShippingPartnerCard: React.FC<{
    partner: ShippingPartner;
    onToggle: () => void;
}> = ({ partner, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [configValues, setConfigValues] = useState<ConfigValues>({});
    const [newListItem, setNewListItem] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [warehouses, setWarehouses] = useState<ParcelHubWarehouse[]>([]);
    const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
    const [warehousesError, setWarehousesError] = useState("");
    const { refetch } = useShippingPartners();
    const toast = useToastStore();

    const config = SHIPPING_PARTNER_CONFIGS[partner.type];
    const isPersisted = !partner._id.includes('default');
    const hasWarehouseField = config?.configFields?.some(field => field.type === "warehouse") ?? false;

    // Initialize config values from partner config
    React.useEffect(() => {
        if (partner.config && config?.configFields) {
            const values: ConfigValues = {};
            config.configFields.forEach(field => {
                const value = partner.config?.[field.key as keyof typeof partner.config];
                if (field.type === "list") {
                    values[field.key] = Array.isArray(value) ? value : [];
                } else if (field.type === "warehouse") {
                    values[field.key] = typeof value === 'number' ? value : "";
                } else {
                    values[field.key] = typeof value === 'string' ? value : "";
                }
            });
            setConfigValues(values);
        }
    }, [partner.config, config?.configFields]);

    // Warehouses live in the courier account, so they can only be listed once the
    // partner has been saved with working credentials.
    React.useEffect(() => {
        if (!isEditing || !hasWarehouseField || !isPersisted) return;

        let isActive = true;
        setIsLoadingWarehouses(true);
        setWarehousesError("");

        ShippingService.listParcelHubWarehouses(partner._id)
            .then(result => {
                if (!isActive) return;
                setWarehouses(result || []);
            })
            .catch(error => {
                if (!isActive) return;
                setWarehouses([]);
                setWarehousesError(error instanceof Error ? error.message : "Unable to load warehouses");
            })
            .finally(() => {
                if (isActive) setIsLoadingWarehouses(false);
            });

        return () => { isActive = false; };
    }, [isEditing, hasWarehouseField, isPersisted, partner._id]);

    if (!config) return null;

    // List-type fields (e.g. multiple pickup locations) are optional - the backend falls back sensibly if none are set.
    // Warehouse fields are optional at save time too: the list can only be fetched once the
    // partner exists with working credentials, so it's picked on a second pass. The backend
    // rejects a ship attempt that has no warehouse set.
    const requiredFields = config.configFields?.filter(
        field => field.type !== "list" && field.type !== "warehouse"
    ) ?? [];

    const handleSave = async () => {
        // Validate required fields
        if (config.requiresConfig) {
            const missingFields = requiredFields.filter(field => isBlank(configValues[field.key]));
            if (missingFields.length > 0) {
                toast.showToast(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`, "error");
                return;
            }
        }

        setIsSaving(true);
        try {
            switch (partner.type) {
                case ShippingPartnerType.DELHIVERY:
                case ShippingPartnerType.SHIPROCKET:
                case ShippingPartnerType.PARCELHUB: {
                    const payload = toConfigPayload(configValues, config.configFields);
                    // Check if this is a default partner (not from backend) - need to create
                    if (!isPersisted) {
                        await ShippingService.create({
                            type: partner.type,
                            enabled: false,
                            config: payload
                        });
                        toast.showToast(`${config.name} created successfully`, "success");
                    } else {
                        // Update existing partner
                        await ShippingService.update(partner._id, { config: payload });
                        toast.showToast(`${config.name} configuration updated successfully`, "success");
                    }
                    break;
                }
                default:
                    throw new Error(`Unsupported shipping partner type: ${partner.type}`);
            }

            await refetch();
            setIsEditing(false);
        } catch (error) {
            console.error(`Failed to save ${config.name} config:`, error);
            const message = error instanceof Error ? error.message : "";
            toast.showToast(
                message || `Failed to save ${config.name} configuration. Please try again.`,
                "error"
            );
        } finally {
            setIsSaving(false);
        }
    };

    const isConfigured = config.requiresConfig
        ? requiredFields.every(field => !isBlank(partner.config?.[field.key as keyof typeof partner.config]))
        : true;

    const handleConfigChange = (key: string, value: string) => {
        setConfigValues(prev => ({ ...prev, [key]: value }));
    };

    const handleAddListItem = (key: string) => {
        const value = (newListItem[key] || "").trim();
        if (!value) return;
        setConfigValues(prev => {
            const existing = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
            if (existing.includes(value)) return prev;
            return { ...prev, [key]: [...existing, value] };
        });
        setNewListItem(prev => ({ ...prev, [key]: "" }));
    };

    const handleRemoveListItem = (key: string, value: string) => {
        setConfigValues(prev => {
            const existing = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
            return { ...prev, [key]: existing.filter(item => item !== value) };
        });
    };

    return (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full ${config.iconBgColor} flex-shrink-0 flex items-center justify-center`}>
                            <div className={config.iconColor}>
                                {config.icon}
                            </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                    </div>
                    {/* Show different UI based on whether partner exists in backend or not */}
                    {partner._id.includes('default') ? (
                        // Default partner - show Add button
                        !isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 text-sm font-medium"
                            >
                                Add
                            </button>
                        )
                    ) : (
                        // Existing partner from backend - show enable/disable toggle
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${partner.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                {partner.enabled ? 'Active' : 'Inactive'}
                            </span>
                            <button
                                onClick={onToggle}
                                disabled={isSaving}
                                className={`relative w-14 h-7 rounded-full transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${partner.enabled ? "bg-blue-600" : "bg-gray-200"
                                    }`}
                            >
                                <div
                                    className={`absolute w-5 h-5 bg-white rounded-full shadow-md top-1 transition-all duration-200 ${partner.enabled ? "right-1" : "left-1"
                                        }`}
                                ></div>
                            </button>
                        </div>
                    )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{config.description}</p>

                {isEditing && config.requiresConfig ? (
                    <div className="space-y-4">
                        {config.configFields?.map(field => (
                            <div key={field.key}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                {field.type === "list" ? (
                                    <div>
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {((configValues[field.key] as string[]) || []).map(item => (
                                                <span
                                                    key={item}
                                                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-sm px-2.5 py-1 rounded-full"
                                                >
                                                    {item}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveListItem(field.key, item)}
                                                        className="text-blue-400 hover:text-blue-700"
                                                        aria-label={`Remove ${item}`}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                            {((configValues[field.key] as string[]) || []).length === 0 && (
                                                <span className="text-sm text-gray-400">No locations added yet</span>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={newListItem[field.key] || ""}
                                                onChange={(e) => setNewListItem(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleAddListItem(field.key);
                                                    }
                                                }}
                                                placeholder={field.placeholder}
                                                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleAddListItem(field.key)}
                                                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                ) : field.type === "warehouse" ? (
                                    isLoadingWarehouses ? (
                                        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                            Loading warehouses...
                                        </div>
                                    ) : warehouses.length > 0 ? (
                                        <select
                                            value={String(configValues[field.key] ?? "")}
                                            onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select a warehouse</option>
                                            {warehouses.map(warehouse => (
                                                <option key={String(warehouse.warehouse_id)} value={String(warehouse.warehouse_id)}>
                                                    {warehouseLabel(warehouse)}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div>
                                            <input
                                                type="number"
                                                value={String(configValues[field.key] ?? "")}
                                                onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                                placeholder={field.placeholder}
                                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            {warehousesError && (
                                                <p className="text-xs text-red-600 mt-1">{warehousesError}</p>
                                            )}
                                        </div>
                                    )
                                ) : (
                                    <input
                                        type={field.type}
                                        value={(configValues[field.key] as string) || ""}
                                        onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                        placeholder={field.placeholder}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                )}
                                {field.example && (
                                    <p className="text-xs text-gray-500 mt-1">{field.example}</p>
                                )}
                            </div>
                        ))}
                        <div className="flex gap-3">
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : !partner._id.includes('default') && isConfigured && config.requiresConfig ? (
                    // Only show configuration for existing partners from backend
                    <div className="space-y-4">
                        <div className="border border-blue-200 rounded-lg p-4">
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-blue-800">Configuration</h3>
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 text-sm font-medium"
                                    >
                                        Edit
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {config.configFields?.map(field => {
                                        const value = partner.config?.[field.key as keyof typeof partner.config];
                                        let displayValue: string;
                                        if (field.type === "password") {
                                            displayValue = value ? "••••••••" : "Not configured";
                                        } else if (field.type === "list") {
                                            displayValue = Array.isArray(value) && value.length > 0
                                                ? value.join(", ")
                                                : "Not configured";
                                        } else if (field.type === "warehouse") {
                                            const match = warehouses.find(w => String(w.warehouse_id) === String(value));
                                            displayValue = isBlank(value as ConfigValue)
                                                ? "Not configured"
                                                : match ? warehouseLabel(match) : `ID ${value}`;
                                        } else {
                                            displayValue = (value as string) || "Not configured";
                                        }
                                        return (
                                            <p key={field.key} className="text-sm text-blue-700">
                                                {field.label}: {displayValue}
                                            </p>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {config.infoMessage && (
                    <div className={`${config.infoMessage.bgColor} border ${config.infoMessage.borderColor} rounded-lg p-4 mt-4`}>
                        <div className="flex items-start gap-3">
                            <AlertCircle className={`h-5 w-5 ${config.infoMessage.iconColor} mt-0.5 flex-shrink-0`} />
                            <div>
                                <h4 className={`text-sm font-medium ${config.infoMessage.textColor.replace('700', '800')} mb-1`}>
                                    {config.infoMessage.title}
                                </h4>
                                <p className={`text-sm ${config.infoMessage.textColor}`}>
                                    {config.infoMessage.message}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const ShippingPartnersManagement = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: "", message: "", onConfirm: () => { } });
    const { data: shippingPartners, isLoading, isError, refetch } = useShippingPartners();
    const toast = useToastStore();

    const handleToggleShippingPartner = async (partnerId: string, isEnabled: boolean) => {
        setModalConfig({
            title: "Confirm Toggle",
            message: `Are you sure you want to ${isEnabled ? "disable" : "enable"} this shipping partner?`,
            onConfirm: async () => {
                try {
                    await ShippingService.enableDisableShippingPartner(partnerId, !isEnabled);
                    refetch();
                    toast.showToast(`Shipping partner ${!isEnabled ? "enabled" : "disabled"} successfully`, "success");
                } catch (error) {
                    console.error("Failed to toggle shipping partner:", error);
                    toast.showToast("Failed to update shipping partner status. Please try again.", "error");
                } finally {
                    setModalOpen(false);
                }
            },
        });
        setModalOpen(true);
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    if (isError) {
        return <div className="flex justify-center items-center h-screen text-red-600">Error loading shipping partners</div>;
    }

    // Create default shipping partners if not provided by backend
    const defaultShippingPartners: ShippingPartner[] = [
        {
            _id: "delhivery-default",
            entityId: 1,
            name: "Delhivery",
            enabled: false,
            type: ShippingPartnerType.DELHIVERY,
            config: {},
            createdAt: new Date(),
            updatedAt: new Date()
        },
        {
            _id: "shiprocket-default",
            entityId: 1,
            name: "Shiprocket",
            enabled: false,
            type: ShippingPartnerType.SHIPROCKET,
            config: {},
            createdAt: new Date(),
            updatedAt: new Date()
        },
        {
            _id: "parcelhub-default",
            entityId: 1,
            name: "Parcel Hub",
            enabled: false,
            type: ShippingPartnerType.PARCELHUB,
            config: {},
            createdAt: new Date(),
            updatedAt: new Date()
        }
        // Add more default shipping partners here as needed
    ];

    // Always show one card per known partner type, using the backend-configured
    // version if one exists, otherwise the "not yet added" placeholder - so
    // configuring one partner doesn't hide the option to add another.
    const displayPartners = defaultShippingPartners.map(defaultPartner => {
        const backendPartner = shippingPartners?.find((partner: ShippingPartner) => partner.type === defaultPartner.type);
        return backendPartner || defaultPartner;
    });

    return (
        <div className="bg-gray-50 min-h-screen">
            <Modal
                isOpen={modalOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={() => setModalOpen(false)}
            />
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                {displayPartners.map((partner) => (
                    <ShippingPartnerCard
                        key={partner._id}
                        partner={partner}
                        onToggle={() => handleToggleShippingPartner(partner._id, partner.enabled)}
                    />
                ))}
            </div>
        </div>
    );
};

export default ShippingPartnersManagement;
